import React, { useState } from 'react';

import { FeaturedIcon } from '@backrs_ui/common-ui';

import { GuideTaskAction } from '@backrs_ui/grow-ui/src/components/GuidePage';
import { CollegeVisitChecklist } from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/content';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Checkbox, SxProps, Theme, Typography } from '@mui/material';

type ChecklistCardProps = Readonly<{
  id: string;
  checklist: CollegeVisitChecklist;
}>;

export const ChecklistCard: React.FC<ChecklistCardProps> = ({ id, checklist }) => {
  const [checkedItems, setCheckedItems] = useState<readonly number[]>([]);

  const toggle = (index: number) =>
    setCheckedItems((current) =>
      current.includes(index) ? current.filter((i) => i !== index) : [...current, index],
    );

  return (
    <Box id={id} sx={cardSx}>
      <Box sx={headerSx}>
        <Box sx={headerTitleSx}>
          <FeaturedIcon
            iconName="CheckDone01"
            size="sm"
            shape="square"
            color="Colors/Text/text-secondary"
          />
          <Typography variant="text-md-semibold" sx={titleSx}>
            {checklist.title}
          </Typography>
        </Box>
        <GuideTaskAction
          id={`${id}-add-task-btn`}
          title={checklist.title}
          description={checklist.items.map((item) => `- ${item}`).join('\n')}
        />
      </Box>
      <Typography variant="text-sm-regular" sx={subtitleSx}>
        {checklist.subtitle}
      </Typography>
      <Box sx={listSx}>
        {checklist.items.map((item, index) => {
          const number = index + 1;
          const checked = checkedItems.includes(index);
          const itemId = `${id}-item-${number}`;

          return (
            <Box key={item} component="label" htmlFor={itemId} sx={rowSx(checked)}>
              <Checkbox
                id={itemId}
                checked={checked}
                onChange={() => toggle(index)}
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckIcon />}
                sx={checkboxSx}
              />
              <Typography variant="text-sm-regular" sx={labelSx}>
                <Box component="span" sx={numberSx}>
                  {number}.
                </Box>{' '}
                {item}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  p: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});

const headerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  mt: backrs.spacing['spacing-md'],
});

const rowSx =
  (checked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-lg'],
    p: backrs.spacing['spacing-xl'],
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${
      backrs.colors[checked ? 'Colors/Border/border-brand' : 'Colors/Border/border-secondary']
    }`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    cursor: 'pointer',
  });

const numberSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  color: backrs.colors['Colors/Text/text-primary'],
});

const checkboxSx: SxProps<Theme> = ({ backrs }) => ({
  p: 0,
  color: backrs.colors['Colors/Border/border-primary'],
  '&.Mui-checked': {
    color: backrs.colors['Colors/Text/text-brand-primary'],
  },
});
